import React from "react";
import { useTranslation } from "react-i18next";
import ProgressBar from "@/components/ui/progress-bar";
import { ArrowLeft, ArrowRight, Check } from "@/components/icons";
import type {
  ExamPreviewQuestion,
  ExamQuestionPreviewTimer,
  ExamQuestionPreviewMode,
} from "./types";
import styles from "./ExamQuestionPreview.module.scss";

export interface ExamQuestionPreviewProps {
  questions: ExamPreviewQuestion[];
  selectedQuestionIndex: number;
  onSelectQuestion: (index: number) => void;
  onPrev: () => void;
  onNext: () => void;
  timerDisplay: ExamQuestionPreviewTimer;
  mode: ExamQuestionPreviewMode;
  /** Sadece take modunda: mevcut soru için seçili seçenek id'leri */
  selectedOptionIds?: string[];
  /** Sadece take modunda: seçim değiştiğinde */
  onOptionChange?: (optionIds: string[]) => void;
  /** Sadece take modunda: soru indeksine göre seçilen seçenek id'leri (sidebar cevaplandı göstergesi) */
  answersByQuestionIndex?: Record<number, string[]>;
  /** Sadece take modunda: backend'e gönderilmiş cevaplar (sidebar'da sadece bunlar işaretli gösterilir) */
  submittedByQuestionIndex?: Record<number, boolean>;
  /** Sadece take modunda: cevabı backend'e gönderir */
  onSubmitAnswer?: (questionId: string, selectedOptionIds: string[]) => void | Promise<void>;
  /** Cevap gönderilirken true (buton loading) */
  isSubmittingAnswer?: boolean;
}

const ExamQuestionPreview: React.FC<ExamQuestionPreviewProps> = ({
  questions,
  selectedQuestionIndex,
  onSelectQuestion,
  onPrev,
  onNext,
  timerDisplay,
  mode,
  selectedOptionIds = [],
  onOptionChange,
  answersByQuestionIndex = {},
  submittedByQuestionIndex,
  onSubmitAnswer,
  isSubmittingAnswer = false,
}) => {
  const { t } = useTranslation();
  const selectedQuestion = questions[selectedQuestionIndex];

  const isTakeMode = mode === "take";

  const handleOptionClick = (optionId: string) => {
    if (!isTakeMode || !onOptionChange || !selectedQuestion) return;
    if (selectedQuestion.allowMultiple) {
      const next = selectedOptionIds.includes(optionId)
        ? selectedOptionIds.filter((id) => id !== optionId)
        : [...selectedOptionIds, optionId];
      onOptionChange(next);
    } else {
      onOptionChange([optionId]);
    }
  };

  const isOptionSelected = (optionId: string) => selectedOptionIds.includes(optionId);

  const showAsCorrect = (opt: { id: string; isCorrect?: boolean }) =>
    isTakeMode ? isOptionSelected(opt.id) : !!opt.isCorrect;

  if (questions.length === 0) return null;

  return (
    <div className={styles.root}>
      <div className={styles.questionsColumns}>
        <div className={styles.questionsListColumn}>
          <div className={styles.previewQuestionsListCard}>
            {questions.map((q, index) => {
              const isActive = index === selectedQuestionIndex;
              const hasAnswer = isTakeMode
                ? (submittedByQuestionIndex != null
                    ? submittedByQuestionIndex[index] === true
                    : (answersByQuestionIndex[index]?.length ?? 0) > 0)
                : q.options.some((o) => o.isCorrect);

              return (
                <button
                  key={q.id}
                  type="button"
                  className={`${styles.previewQuestionItem} ${isActive ? styles.previewQuestionItemActive : ""}`}
                  onClick={() => onSelectQuestion(index)}
                >
                  <span className={styles.previewQuestionIndex}>
                    {index + 1}. {t("createExam.questions.questionShort")}
                  </span>
                  <span className={styles.previewQuestionStatus}>
                    <span
                      className={`${styles.previewQuestionStatusCircle} ${hasAnswer ? styles.previewQuestionStatusCircleAnswered : ""} ${isActive ? styles.previewQuestionStatusCircleActive : ""}`}
                    />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <div className={styles.questionEditorColumn}>
          <div className={styles.previewContent}>
            {selectedQuestion && (
              <>
                <div className={styles.previewProgressRow}>
                  <ProgressBar
                    value={
                      questions.length > 0
                        ? ((selectedQuestionIndex + 1) / questions.length) * 100
                        : 0
                    }
                    showLabel={false}
                    size="small"
                    className={styles.previewProgressBar}
                  />
                  <span className={styles.previewProgressLabel}>
                    {selectedQuestionIndex + 1}/{questions.length}
                  </span>
                </div>
                <section className={styles.previewQuestionCard}>
                  <h3 className={styles.previewQuestionTitle}>
                    {selectedQuestionIndex + 1}.{" "}
                    {t("createExam.questions.questionShort")}
                  </h3>
                  <p className={styles.previewQuestionText}>
                    {selectedQuestion.text ||
                      t("createExam.questions.questionPlaceholder")}
                  </p>
                  {selectedQuestion.imagePreview && (
                    <div className={styles.previewQuestionImage}>
                      <img
                        src={selectedQuestion.imagePreview}
                        alt={t("createExam.questions.questionImage")}
                      />
                    </div>
                  )}
                  {selectedQuestion.allowMultiple && (
                    <p className={styles.previewQuestionHint}>
                      {t("createExam.questions.allowMultiple")}
                    </p>
                  )}
                  <ul className={styles.previewOptionsList}>
                    {selectedQuestion.options.map((opt) => {
                      const selected = showAsCorrect(opt);
                      const clickable = isTakeMode;
                      return (
                        <li
                          key={opt.id}
                          role={clickable ? "button" : undefined}
                          tabIndex={clickable ? 0 : undefined}
                          className={`${styles.previewOptionItem} ${clickable ? styles.previewOptionItemClickable : ""} ${selected ? (isTakeMode ? styles.previewOptionItemSelected : styles.previewOptionItemCorrect) : ""}`}
                          onClick={() => clickable && handleOptionClick(opt.id)}
                          onKeyDown={(e) => {
                            if (clickable && (e.key === "Enter" || e.key === " ")) {
                              e.preventDefault();
                              handleOptionClick(opt.id);
                            }
                          }}
                        >
                          <span className={styles.previewOptionRadio}>
                            {selected && (
                              <Check width={14} height={14} color="#15AC77" />
                            )}
                          </span>
                          <span className={styles.previewOptionText}>
                            {opt.text ||
                              t("createExam.questions.answerPlaceholder")}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  {isTakeMode && onSubmitAnswer && (
                    <div className={styles.previewContinueWrap}>
                      <button
                        type="button"
                        className={styles.previewContinueButton}
                        onClick={() =>
                          onSubmitAnswer(selectedQuestion.id, selectedOptionIds)
                        }
                        disabled={
                          isSubmittingAnswer || selectedOptionIds.length === 0
                        }
                      >
                        {isSubmittingAnswer
                          ? t("common.loading")
                          : selectedQuestionIndex === questions.length - 1
                            ? t("clientExams.takeExam.complete")
                            : t("clientExams.takeExam.submitAnswer")}
                      </button>
                    </div>
                  )}
                  <div className={styles.previewBottomNav}>
                    <button
                      type="button"
                      className={styles.previewNavButton}
                      onClick={onPrev}
                      disabled={selectedQuestionIndex === 0}
                    >
                      <ArrowLeft width={18} height={18} />
                    </button>
                    <div className={styles.previewCenterInfo}>
                      <div className={styles.previewTimer}>
                        <div className={styles.previewTimerBox}>
                          {timerDisplay.hours}
                        </div>
                        <span className={styles.previewTimerSeparator}>:</span>
                        <div className={styles.previewTimerBox}>
                          {timerDisplay.minutes}
                        </div>
                        <span className={styles.previewTimerSeparator}>:</span>
                        <div className={styles.previewTimerBox}>
                          {timerDisplay.seconds}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={styles.previewNavButton}
                      onClick={onNext}
                      disabled={selectedQuestionIndex === questions.length - 1}
                    >
                      <ArrowRight width={18} height={18} />
                    </button>
                  </div>
                </section>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExamQuestionPreview;
export type { ExamPreviewQuestion, ExamQuestionPreviewTimer, ExamQuestionPreviewMode } from "./types";
